import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { apiClient } from './api-client';

export const DEFAULT_HOSPITAL_TITLE = 'Hospital';

interface HospitalBrandSource {
  id?: unknown;
  title?: unknown;
  logoDataUrl?: unknown;
  smallLogoDataUrl?: unknown;
}

export interface HospitalBrand {
  title: string;
  logoDataUrl: string | null;
  smallLogoDataUrl: string | null;
}

const imageSource = (value: unknown): string | null => (
  typeof value === 'string' && value.trim() ? value.trim() : null
);

export const hospitalTitleFromSettings = (settings: HospitalBrandSource | null | undefined): string => {
  const title = settings?.title;
  return typeof title === 'string' && title.trim() ? title.trim() : DEFAULT_HOSPITAL_TITLE;
};

export const hospitalBrandFromSettings = (settings: HospitalBrandSource | null | undefined): HospitalBrand => ({
  title: hospitalTitleFromSettings(settings),
  logoDataUrl: imageSource(settings?.logoDataUrl),
  smallLogoDataUrl: imageSource(settings?.smallLogoDataUrl),
});

export const applyHospitalFavicon = (smallLogoDataUrl: string | null): void => {
  const existing = document.head.querySelector<HTMLLinkElement>('#hospital-favicon');
  if (!smallLogoDataUrl) {
    existing?.remove();
    return;
  }

  const favicon = existing ?? document.createElement('link');
  const mimeType = /^data:(image\/(?:png|jpeg|webp));base64,/i.exec(smallLogoDataUrl)?.[1] ?? 'image/png';
  favicon.id = 'hospital-favicon';
  favicon.rel = 'icon';
  favicon.type = mimeType;
  favicon.href = smallLogoDataUrl;
  if (!existing) document.head.append(favicon);
};

/**
 * Provides the hospital name and logos used by public and authenticated UI.
 * The General Settings query is shared through React Query, so saving branding
 * updates every mounted brand surface without a reload.
 */
export const useHospitalBrand = (): HospitalBrand => {
  const brand = useQuery({
    queryKey: ['hospital-brand'],
    queryFn: async () => {
      const response = await apiClient.get<{ success: true; data: HospitalBrand }>('/auth/brand');
      return response.data.data;
    },
    staleTime: 5 * 60_000,
  });
  const hospitalBrand = hospitalBrandFromSettings(brand.data);

  useEffect(() => {
    document.title = hospitalBrand.title;
  }, [hospitalBrand.title]);

  useEffect(() => {
    applyHospitalFavicon(hospitalBrand.smallLogoDataUrl);
  }, [hospitalBrand.smallLogoDataUrl]);

  return hospitalBrand;
};

export const useHospitalTitle = (): string => useHospitalBrand().title;
